/** @format */

import React, { useEffect } from "react";
import {
  ChevronDown,
  Phone,
  Home,
  Palette,
  Sofa,
  Clock,
  ArrowRight,
  Music,
  Settings,
  BookOpen,
  Star,
  Zap,
  Music2Icon,
  List,
  Music3Icon,
  Package2,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { ImageLoader } from "../../../image-loader";
import { CategoryInterface } from "@/types/category";
import { StoreInterface } from "@/types/store";

interface MobileMenuProps {
  categories: CategoryInterface[];
  storeInfo: StoreInterface;
  isOpen: boolean;
  expandedMobileCategory: number | null;
  hoveredParentCategory: number | null;
  onCategoryToggle: (categoryId: number) => void;
  onParentCategoryHover: (categoryId: number) => void;
}

// Quick Actions Configuration
const quickActions = [
  {
    id: "hop-am-guitar",
    name: "Hợp âm guitar",
    href: "/hop-am-guitar",
    icon: Music,
    gradient: "from-purple-500 to-pink-500",
    description: "Học hợp âm guitar cơ bản đến nâng cao",
    badge: "Hot",
  },
  {
    id: "metronome",
    name: "Metronome",
    href: "/metronome",
    icon: Settings,
    gradient: "from-blue-500 to-cyan-500",
    description: "Công cụ gõ nhịp",
    badge: "Hot",
  },
  {
    id: "guitar-nhat-bai",
    name: "Guitar Nhật Bãi",
    href: "/guitar-nhat-bai",
    icon: Settings,
    gradient: "from-blue-500 to-cyan-500",
    description: "Guitar Nhật Bãi",
    badge: "Hot",
  },
  // {
  //   id: "kien-thuc",
  //   name: "Kiến thức",
  //   href: "/kien-thuc",
  //   icon: BookOpen,
  //   gradient: "from-green-500 to-emerald-500",
  //   description: "Bài viết và kiến thức về guitar",
  //   badge: null,
  // },
];

// Animation variants
const containerVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      staggerChildren: 0.08,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.2 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const quickActionVariants: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
};

const megaMenuVariants: Variants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.3 },
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const childrenVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, staggerChildren: 0.05 },
  },
};

export const MobileMenu: React.FC<MobileMenuProps> = ({
  categories,
  storeInfo,
  isOpen,
  expandedMobileCategory,
  hoveredParentCategory,
  onCategoryToggle,
  onParentCategoryHover,
}) => {
  const router = useRouter();
  useEffect(() => {
    if (categories) {
      onCategoryToggle(categories[0].id);
      if (categories[0].subCategories) {
        onParentCategoryHover(categories[0].subCategories[0].id);
      }
    }
  }, []);
  useEffect(() => {
    if (isOpen) {
      // Disable scroll
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Enable scroll and restore position
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  const getCategoryDisplayName = (category: CategoryInterface) => category.name;
  const hasMegaMenu = (category: CategoryInterface) =>
    category.subCategories && category.subCategories.length > 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="lg:hidden bg-background shadow-2xl border-t border-border backdrop-blur-md"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit">
        <div className="max-h-[85vh] overflow-y-auto">
          {/* Categories */}
          <motion.div variants={itemVariants} className="space-y-4">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                variants={itemVariants}
                transition={{ delay: index * 0.05 }}
                className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden hover:shadow-md transition-all duration-300">
                {hasMegaMenu(category) ? (
                  <div>
                    {/* Category Button */}
                    <button
                      onClick={() => {
                        onCategoryToggle(category.id);

                        if (
                          expandedMobileCategory !== category.id &&
                          category.subCategories?.[0]
                        ) {
                          onParentCategoryHover(category.subCategories[0].id);
                        }
                      }}
                      className="w-full flex items-center justify-between p-5 hover:bg-muted transition-all duration-200 group">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shadow-md">
                          <Package2 className="w-4 h-4 text-primary-foreground" />
                        </div>

                        <div className="text-left">
                          <span className="font-semibold text-sm text-foreground block">
                            {getCategoryDisplayName(category)}
                          </span>
                          <span className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {category.subCategories?.length || 0} danh mục con
                          </span>
                        </div>
                      </div>

                      <ChevronDown
                        className={`h-4 w-4 text-primary transition-transform duration-300 ${
                          expandedMobileCategory === category.id
                            ? "rotate-180"
                            : ""
                        }`}
                      />
                    </button>

                    {/* Mega Menu */}
                    <AnimatePresence>
                      {expandedMobileCategory === category.id && (
                        <motion.div
                          variants={megaMenuVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className="border-t border-border bg-muted/30">
                          <div className="flex">
                            {/* Left */}
                            <div className="w-[40%] border-r border-border p-4">
                              <h4 className="text-xs font-semibold text-foreground mb-3 flex items-center gap-2">
                                <List className="w-3 h-3 text-primary" />
                                Danh mục
                              </h4>

                              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                                {category.subCategories?.map((subcategory) => (
                                  <div
                                    key={subcategory.id}
                                    onClick={() => {
                                      if (
                                        subcategory.subCategories?.length > 0
                                      ) {
                                        onParentCategoryHover(subcategory.id);
                                      } else {
                                        router.push(
                                          `/danh-muc/${subcategory.slug}`,
                                        );
                                      }
                                    }}
                                    className={`p-3 rounded-xl cursor-pointer border transition-all duration-300 ${
                                      hoveredParentCategory === subcategory.id
                                        ? "bg-primary/10 border-primary text-primary"
                                        : "border-transparent hover:bg-muted hover:border-border"
                                    }`}>
                                    <div className="flex flex-col items-center text-center gap-2">
                                      <div className="relative h-8 w-8 rounded-lg overflow-hidden">
                                        <ImageLoader
                                          fill
                                          src={subcategory.imageUrl}
                                          alt={subcategory.name}
                                          className="object-cover"
                                        />
                                      </div>

                                      <span className="text-xs font-medium line-clamp-2">
                                        {subcategory.name}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Right */}
                            <div className="w-[60%] p-4">
                              <h4 className="text-xs font-semibold text-foreground mb-4">
                                Chi tiết
                              </h4>

                              <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
                                {hoveredParentCategory &&
                                  category.subCategories
                                    ?.find(
                                      (cat) => cat.id === hoveredParentCategory,
                                    )
                                    ?.subCategories?.map((child) => (
                                      <Link
                                        key={child.id}
                                        href={`/danh-muc/${child.slug}`}
                                        className="group flex flex-col items-center gap-2 p-3 rounded-xl border border-transparent hover:bg-muted hover:border-border transition-all duration-300">
                                        <div className="relative h-8 w-8 rounded-lg overflow-hidden">
                                          <ImageLoader
                                            fill
                                            src={child.imageUrl}
                                            alt={child.name}
                                            className="object-cover"
                                          />
                                        </div>

                                        <span className="text-[11px] text-center font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                                          {child.name}
                                        </span>
                                      </Link>
                                    ))}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    href={`/danh-muc/${category.slug}`}
                    className="flex items-center gap-4 p-5 hover:bg-muted transition-all duration-300 group">
                    <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shadow-md">
                      <Package2 className="w-4 h-4 text-primary-foreground" />
                    </div>

                    <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                      {getCategoryDisplayName(category)}
                    </span>

                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 ml-auto" />
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            variants={itemVariants}
            className="px-4 py-6 border-t border-border bg-background">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Công cụ nhanh
            </h3>

            <div className="grid gap-3">
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.id}
                  variants={quickActionVariants}
                  transition={{ delay: index * 0.1 }}>
                  <Link
                    href={action.href}
                    className="group flex items-center gap-4 p-4 rounded-2xl border border-border bg-card hover:bg-muted transition-all duration-300">
                    <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center">
                      <action.icon className="w-4 h-4 text-primary-foreground" />
                    </div>

                    <div className="flex-1">
                      <div className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                        {action.name}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {action.description}
                      </p>
                    </div>

                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="h-6" />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
